import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Button } from "@twilio-paste/core/button";

import { sessionDataHandler } from "../sessionDataHandler";
import contactBackend from "../utils/contactBackend";
import { endSession } from "../store/actions/genericActions";
import { AppState } from "../store/definitions";
import { containerStyles, textStyles, titleStyles } from "./styles/ConversationEnded.styles";
import type { Transcript } from "./Transcript";
import Rating from "./customized/Rating";
import { recentChatsStorage } from "../utils/recentChatsStorage";

export const ConversationEnded = () => {
    const dispatch = useDispatch();
    const { messages, users, preEngagementData, transcriptConfig, conversationState } = useSelector(
        (state: AppState) => ({
            messages: state.chat.messages,
            users: state.chat.users,
            preEngagementData: state.chat.conversation?.attributes.pre_engagement_data,
            transcriptConfig: state.config.transcript,
            conversationState: state.chat.conversationState
        })
    );

    const conversationSid = useSelector((state: AppState) => state.session.conversationSid);
    const email = useSelector((state: AppState) => state.session.preEngagementData?.Email);

    useEffect(() => {
        if (!conversationSid) {
            return;
        }
        const lastMessage = messages?.[messages.length - 1];
        const formData = (preEngagementData as any)?.formData || {};
        const service = formData.service || {};
        recentChatsStorage.upsert(email || (preEngagementData as any)?.customerEmail, {
            conversationSid,
            state: conversationState,
            dateUpdated: new Date().toISOString(),
            friendlyName: (preEngagementData as any)?.friendlyName || (preEngagementData as any)?.customerName,
            serviceType: service.ServiceType || "",
            preview: lastMessage?.body || ""
        });
    }, [conversationSid, conversationState, email, messages, preEngagementData]);

    const handleStartNewChat = () => {
        sessionDataHandler.clear();
        dispatch(endSession() as never);
    };

    const [rating, setRating] = useState<number | null>(null);
    const [isSurveySubmitted, setIsSurveySubmitted] = useState<boolean>(false);

    const handleSurveySubmit = async () => {
        // 一点击就过期并清掉本地 Session，避免提交后立刻关微信仍自动进窗
        sessionDataHandler.clear();

        try {
            const resp = await contactBackend<{ code?: number; message?: string; isSuccess?: boolean }>("/score", {
                score: rating,
                conversationSid
            });
            // 后端 HTTP 200 即视为成功（SF 体里字段不统一）
            const failed = resp?.isSuccess === false || (typeof resp?.code === "number" && resp.code >= 400);
            if (failed) {
                console.warn("评分接口返回失败", resp);
            }
            setIsSurveySubmitted(true);
            window.setTimeout(() => {
                dispatch(endSession() as never);
            }, 1000);
        } catch (e) {
            console.error("提交评分失败", e);
            dispatch(endSession() as never);
        }
    };

    let TranscriptComponent: typeof Transcript | undefined;

    if (process.env.DOWNLOAD_TRANSCRIPT_ENABLED === "true" || process.env.EMAIL_TRANSCRIPT_ENABLED === "true") {
        // eslint-disable-next-line @typescript-eslint/no-require-imports,@typescript-eslint/no-var-requires,global-require
        ({ Transcript: TranscriptComponent } = require("./Transcript"));
    }

    return (
        <Box {...containerStyles}>
            <Text as="h3" {...titleStyles}>
                如果没有其他问题，请您稍后对我的服务做出评价，非常满意请按3，感谢您对我工作的支持与认可！祝您工作顺利，生活愉快！
            </Text>
            {TranscriptComponent ? (
                <TranscriptComponent
                    messages={messages}
                    preEngagementData={preEngagementData}
                    users={users}
                    transcriptConfig={transcriptConfig}
                />
            ) : (
                <Fragment />
            )}
            {!isSurveySubmitted && (
                <>
                    <Text as="p" {...textStyles}>
                        Please rate your experience: {conversationSid}
                    </Text>
                    <Rating onRate={setRating} />
                    <Text as="p" {...textStyles} />

                    <Button variant="primary" onClick={handleSurveySubmit} disabled={rating === null}>
                        Submit Rating
                    </Button>
                </>
            )}
            {isSurveySubmitted && (
                <Text as="p" {...textStyles}>
                    Thank you for your rating!
                </Text>
            )}
            <Text as="p" {...textStyles}>
                If you have any more questions, feel free to reach out again.
            </Text>
            <Button variant="primary" data-test="start-new-chat-button" onClick={handleStartNewChat}>
                Start new chat
            </Button>
        </Box>
    );
};
